import { Component, Input, NgModule, SimpleChanges } from '@angular/core';
import { BehaviorSubject, combineLatest, EMPTY, merge, Subject, map, startWith, switchMap, tap, withLatestFrom } from 'rxjs';
import { UntypedFormControl } from '@angular/forms';
import { TargetType, targetTypes } from '@uw/core/models/target-type.model';
import { SuggestedAmount } from '@uw/core/models/suggested-amount.model';
import { SubHeaderService } from '@uw/themes/services/subheader.service';
import { MatDialog } from '@angular/material/dialog';
import { SuggestedAmountDetailComponent, SuggestedAmountDetailModule } from '@uw/shared/components/suggested-amount-detail/suggested-amount-detail.component';
import { KeyValueModel } from '@uw/core/models/keyvalue.model';
import { CommonModule, AsyncPipe } from '@angular/common';
import { MatTabsModule, MatTabNav, MatTabLink, MatTabNavPanel } from '@angular/material/tabs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SuggestedAmountListModule } from '../suggested-amount-list';
import { SuggestedAmountsEntityService } from '@uw/core/services/suggested.amounts.entity.service';
import { SuggestedAmountListComponent } from '../suggested-amount-list/suggested-amount-list.component';

@Component({
    selector: 'uw-entity-suggested-amounts',
    templateUrl: './suggested-amounts.component.html',
    styleUrls: ['./suggested-amounts.component.scss'],
    imports: [MatTabNav, MatTabLink, MatTabNavPanel, SuggestedAmountListComponent, AsyncPipe, TranslateModule]
})
export class SuggestedAmountsComponent {
 
  @Input()
  entityId!: string | null;

  @Input()
  languages!: KeyValueModel[];
 
  private readonly _addOrUpdateGivingAmountSubject: Subject<SuggestedAmount | null> =
    new Subject();

  private readonly _addGivingAmount$ =
    this._addOrUpdateGivingAmountSubject.asObservable();

  private readonly _selectTargetTypeSubject: BehaviorSubject<TargetType> =
    new BehaviorSubject(targetTypes[0]);

  private readonly _selectTargetType$ =
    this._selectTargetTypeSubject.asObservable();

  private readonly _refreshSubject: Subject<void> = new Subject();

  private readonly _refresh$ = this._refreshSubject.pipe(
    withLatestFrom(this._selectTargetType$),
    map(([_, selectedTargetType]) => selectedTargetType)
  );

  private readonly _saveSubject: Subject<SuggestedAmount> = new Subject();

  private readonly _save$ = this._saveSubject.asObservable();

  private readonly _selectSuggestedAmountSubject: Subject<SuggestedAmount | null> =
    new Subject();

  private readonly _selectSuggestedAmount$ =
    this._selectSuggestedAmountSubject.asObservable();

  private readonly _deleteSubject: Subject<SuggestedAmount> = new Subject();

  private readonly _delete$ = this._deleteSubject.asObservable();

  private readonly _entityId$ = new BehaviorSubject<string | null>(null);

  readonly vm$ = merge(this._selectTargetType$, this._refresh$).pipe(
    switchMap((selectedTargetType) =>
      combineLatest([
        this._entityId$.pipe(
          switchMap((entityId) =>
            this._suggestedAmountsEntityService.getSuggestedAmounts(
              entityId,
              selectedTargetType.value
            )
          ),
          map((suggestedAmounts) =>
            suggestedAmounts.filter(
              (x) => x.targetTypeCode == selectedTargetType.value
            )
          ),
          map((suggestedAmounts) => ({
            selectedTargetType,
            suggestedAmounts,
            targetTypes,
          }))
        ),
        this._selectSuggestedAmount$.pipe(startWith(null)),        
        this._entityId$.pipe(
          switchMap((entityId) =>
            this._save$.pipe(
              switchMap((suggestedAmount) =>
                this._handleSave(entityId, selectedTargetType, suggestedAmount)
              )
            )
          ),
          startWith(null)
        ),
        this._entityId$.pipe(
          switchMap((entityId) =>
            this._delete$.pipe(
              switchMap((suggestedAmount) =>
                this._handleDelete(
                  entityId,
                  selectedTargetType,
                  suggestedAmount
                )
              )
            )
          ),
          startWith(null)
        ),
        this._entityId$.pipe(
          switchMap((entityId) =>
            this._addGivingAmount$.pipe(
              switchMap((suggestedAmount) =>
                this._dialog
                  .open(SuggestedAmountDetailComponent, {
                    data: {
                      entityId: entityId,
                      suggestedAmount,
                      targetTypeCode: selectedTargetType,
                      languages: this.languages,
                    },
                    panelClass: 'uw-dialog-panel',
                  })
                  .afterClosed()
                  .pipe(
                    tap((suggestedAmount: SuggestedAmount) => {
                      return suggestedAmount
                        ? this._refreshSubject.next()
                        : EMPTY;
                    })
                  )
              ),
              startWith(null)
            )
          )
        ),
      ])
    ),
    map(
      ([
        { selectedTargetType, suggestedAmounts, targetTypes },
        selectedSuggestedAmount
      ]) => {
        this._subHeaderService.setSubTitle(this._translateService.instant('common.suggested_amounts'));

        const formControl = new UntypedFormControl(selectedSuggestedAmount, []);

        return {
          selectedTargetType,
          suggestedAmounts,
          targetTypes,
          formControl,
        };
      }
    )
  );

  constructor(
    private readonly _suggestedAmountsEntityService : SuggestedAmountsEntityService,
    private readonly _subHeaderService: SubHeaderService,
    private readonly _dialog: MatDialog,
    private readonly _translateService: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.entityId && changes.entityId.currentValue) {
      this._entityId$.next(this.entityId!);
    }
  }

  onSelectTargetType(targetType: TargetType) {
    this._selectTargetTypeSubject.next(targetType);
  }

  onSave(suggestedAmount: any) {
    this._saveSubject.next(suggestedAmount);
  }

  onEdit(suggestedAmount: any) {
    this._addOrUpdateGivingAmountSubject.next(suggestedAmount);
  }

  onDelete(suggestedAmount: any) {
    this._deleteSubject.next(suggestedAmount);
  }

  onAddGivingAmount() {
    this._addOrUpdateGivingAmountSubject.next(null);
  }

  private _handleDelete(
    entityId: any,
    targetType: TargetType,
    suggestedAmount: any
  ) {
    return this._suggestedAmountsEntityService
      .deleteSuggestedAmount(
        entityId,
        targetType.value,
        suggestedAmount.suggestAmountId
      )
      .pipe(
        tap((_) => this._refreshSubject.next()),
        startWith(null)
      );
  }

  private _handleSave(
    entityId: any,
    targetType: TargetType,
    suggestedAmount: SuggestedAmount
  ) {
    const obs$ = suggestedAmount.suggestAmountId
      ? this._suggestedAmountsEntityService.updateSuggestedAmount(
          entityId,
          targetType.value,
          suggestedAmount
        )
      : this._suggestedAmountsEntityService.createSuggestedAmount(
          entityId,
          targetType.value,
          suggestedAmount
        );

    return obs$.pipe(
      tap(
        (_) => {
          this._selectSuggestedAmountSubject.next(null);
          this._refreshSubject.next();
        }
      )
    );
  }
}

@NgModule({
    exports: [SuggestedAmountsComponent],
    imports: [
        CommonModule,
        MatTabsModule,
        TranslateModule,
        SuggestedAmountListModule,
        SuggestedAmountDetailModule,
        SuggestedAmountsComponent,
    ],
    providers: [SuggestedAmountsEntityService],
})
export class SuggestedAmountsModule {}
